import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Plus, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadPdf } from "@/lib/download";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const STYLE = {
  ok: ["bg-emerald-500", "Innsendt i tide"],
  late: ["bg-amber-400", "Innsendt for sent"],
  missing: ["bg-red-500", "Ikke innsendt"],
  pending: ["bg-slate-200", "Perioden pågår / frist ikke passert"],
  na: ["bg-slate-100 border border-dashed border-slate-300", "Ikke registrert ennå"],
};

const Legend = () => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
    {Object.entries(STYLE).map(([k, [cls, l]]) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm ${cls}`} />{l}</span>)}
  </div>
);

const Cell = ({ cell, onClick, testId }) => {
  const [cls, label] = STYLE[cell.status];
  return (
    <td className="px-1 py-1.5 text-center">
      <button type="button" onClick={onClick} title={cell.pax != null ? `${label} · ${cell.pax} passasjerer` : label} data-testid={testId}
        className={`w-full min-w-[44px] h-8 rounded ${cls} text-[11px] font-semibold ${cell.status === "ok" || cell.status === "missing" ? "text-white" : "text-slate-700"} hover:opacity-80 transition-opacity`}>
        {cell.pax != null ? cell.pax : ""}
      </button>
    </td>
  );
};

const MONTHS = ["januar", "februar", "mars", "april", "mai", "juni", "juli", "august", "september", "oktober", "november", "desember"];

const MonthButton = ({ cid }) => {
  const opts = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return { value: `${d.getFullYear()}-${m}`, label: `${MONTHS[d.getMonth()][0].toUpperCase()}${MONTHS[d.getMonth()].slice(1)} ${d.getFullYear()}` };
  });
  const [month, setMonth] = useState(opts[1].value);
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await downloadPdf(`/customers/${cid}/pax/month.pdf`, { month }, `maanedsrapport-${month}.pdf`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={month} onValueChange={setMonth}>
        <SelectTrigger className="w-44 h-9" data-testid="pax-month-select"><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={dl} disabled={busy} data-testid="pax-month-download">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Månedsrapport til faktura (PDF)</Button>
    </div>
  );
};

const ReportButton = ({ cid, periods }) => {
  const due = periods.filter((p) => p.due);
  const [start, setStart] = useState(due[0]?.start || periods[0]?.start);
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/customers/${cid}/pax/report.pdf`, { params: { start }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `perioderapport-${start}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={start} onValueChange={setStart}>
        <SelectTrigger className="w-56 h-9" data-testid="pax-report-period"><SelectValue /></SelectTrigger>
        <SelectContent>{periods.map((p) => <SelectItem key={p.start} value={p.start}>{p.label}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" onClick={dl} disabled={busy} data-testid="pax-report-download">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Last ned perioderapport (PDF)</Button>
    </div>
  );
};

export const PaxOverview = ({ cid, refreshKey, onOpen, onManual }) => {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get(`/customers/${cid}/pax/overview`, { params: { n: 8 } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [cid]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (data) onManual?.ready(data); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post(`/customers/${cid}/pax/actors`, { name: name.trim() });
      setName("");
      toast.success("Rederiet er lagt til i oversikten");
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  if (!data) return <div className="h-40 rounded-lg bg-white border border-slate-200 animate-pulse" />;
  const s = data.summary;
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="pax-overview">
      <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        {s ? (
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="pax-summary">
            <span className="text-slate-500">Siste frist ({s.label}):</span>
            <span className="rounded-full bg-emerald-50 text-emerald-800 px-2.5 py-0.5 font-medium">{s.ok} i tide</span>
            <span className="rounded-full bg-amber-50 text-amber-800 px-2.5 py-0.5 font-medium">{s.late} for sent</span>
            <span className="rounded-full bg-red-50 text-red-700 px-2.5 py-0.5 font-medium">{s.missing} mangler</span>
          </div>
        ) : <span className="text-sm text-slate-500">Ingen frister har passert ennå</span>}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => onManual?.open()} data-testid="pax-manual-open"><Plus className="w-4 h-4 mr-1" />Registrer rapport manuelt</Button>
          <ReportButton cid={cid} periods={data.periods} key={data.periods[0]?.start} />
          <MonthButton cid={cid} />
          <Button variant="ghost" size="sm" onClick={load} data-testid="pax-overview-refresh"><RotateCcw className="w-4 h-4" /></Button>
        </div>
      </div>
      {data.actors.length === 0 ? (
        <p className="p-6 text-sm text-slate-500" data-testid="pax-empty">Ingen rederier ennå. De legges til automatisk ved første innsending, eller du kan legge dem inn under.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500">
                <th className="text-left font-medium px-4 py-2 sticky left-0 bg-white min-w-[160px]">Rederi</th>
                {data.periods.map((p) => <th key={p.start} className="font-medium px-1 py-2 whitespace-nowrap" title={`Frist ${p.deadline_label}`}>{p.label.split(" · ")[0]}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.actors.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onOpen(a.id)} data-testid={`pax-actor-row-${a.id}`}>
                  <td className="px-4 py-2 sticky left-0 bg-white font-medium text-slate-900">{a.name}<div className="text-xs font-normal text-slate-500">{a.email || (a.auto ? "" : "Lagt inn manuelt")}</div></td>
                  {a.cells.map((cell, i) => (
                    <Cell key={i} cell={cell} testId={`pax-cell-${a.id}-${i}`} onClick={(e) => { e.stopPropagation(); onOpen(a.id, cell.sub_id); }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-4 sm:p-5 border-t border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <Legend />
        <form onSubmit={add} className="flex gap-2 w-full lg:w-auto">
          <Input placeholder="Legg til rederi manuelt" value={name} onChange={(e) => setName(e.target.value)} className="lg:w-64" data-testid="pax-add-actor-input" />
          <Button type="submit" variant="outline" data-testid="pax-add-actor-button"><Plus className="w-4 h-4 mr-1" />Legg til</Button>
        </form>
      </div>
    </div>
  );
};
